"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";

import type { ReplayMetricKey, ReplayPlayerMetrics } from "@/entities/replay-metrics";
import {
  formatMetricValue,
  MetricsPlayerLabel,
  useTournamentReplayMetrics,
} from "@/entities/replay-metrics";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { cn } from "@/shared/lib/utils";
import { RoleIcon } from "@/shared/ui/data-display/role-icon";
import { scoreColor } from "@/shared/lib/score-color";
import { SectionHeading } from "./section-heading";

/**
 * KAN-810: топы по поведению с фильтром роли (эпик KAN-804).
 *
 * Прежний вид (KAN-728) выкладывал семь метрик × пять ролей одной колонкой: чтобы
 * найти лучшего лесника по вижену, приходилось прочитать почти весь блок. Здесь
 * роль выбирается сверху, и все семь бордов сразу о ней.
 *
 * ТРИ ПРАВИЛА KAN-728 ОСТАЮТСЯ, И ИХ НЕЛЬЗЯ ОСЛАБЛЯТЬ РАДИ ПЛОТНОСТИ ВИТРИНЫ.
 *
 * 1. Топ считается ВНУТРИ РОЛИ. Лесник обгонит мидера по урону в объекты просто
 *    потому, что он лесник: общий топ по такой метрике состоял бы из одних лесников
 *    и ничего не сообщал.
 *
 * 2. В топ идут только игроки с посчитанным перцентилем. Бэкенд отдаёт его лишь
 *    тем, у кого набралась выборка и есть с кем сравниваться (KAN-725). Игрок с
 *    одной удачной игрой не должен возглавлять турнир.
 *
 * 3. Метрика без пригодных игроков не показывается вовсе. Пустой топ хуже
 *    отсутствующего: он читается как «никто ничего не делал».
 */

/** Метрики, для которых «больше» — осмысленное утверждение о стиле. */
const BOARD_KEYS: ReplayMetricKey[] = [
  "shotcalling",
  "objectiveFocus",
  "towerDiveAggression",
  "visionQuality",
  "goldUtilization",
  "ccPressure",
];

/** Сколько игроков в каждом топе. */
const TOP_SIZE = 3;

/** Порядок ролей в переключателе — привычный порядок линий. */
const ROLE_ORDER = ["TOP", "JUNGLE", "MIDDLE", "BOTTOM", "UTILITY"];

interface LeaderRow {
  player: ReplayPlayerMetrics;
  /** Сырое значение метрики — то, что реально измерено. */
  value: number;
  /** Положение внутри роли, 0..100 — по нему шкала и цвет. */
  score: number;
}

/**
 * Раскладывает игроков по ролям и метрикам. Роль берётся из группы сравнения
 * (`peerGroupRole`), а не из позиции игрока: сравнение считал бэкенд, и подписывать
 * топ другой ролью, чем та, внутри которой он посчитан, было бы враньём.
 */
function buildBoards(
  players: ReplayPlayerMetrics[],
): Map<string, Map<ReplayMetricKey, LeaderRow[]>> {
  const byRole = new Map<string, Map<ReplayMetricKey, LeaderRow[]>>();

  for (const key of BOARD_KEYS) {
    for (const player of players) {
      const metric = player.metrics.find((m) => m.key === key);
      // Перцентиль null — выборка не набрана либо сравнивать не с кем.
      if (!metric || metric.value === null || metric.percentile === null) continue;

      const role = metric.peerGroupRole ?? player.position;
      if (!role) continue;

      const normalized = role.trim().toUpperCase();
      const boards = byRole.get(normalized) ?? new Map<ReplayMetricKey, LeaderRow[]>();
      const rows = boards.get(key) ?? [];

      rows.push({
        player,
        value: metric.value,
        score: Math.round(metric.percentile * 100),
      });

      boards.set(key, rows);
      byRole.set(normalized, boards);
    }
  }

  for (const boards of byRole.values()) {
    for (const [key, rows] of boards) {
      boards.set(
        key,
        rows.sort((a, b) => b.value - a.value).slice(0, TOP_SIZE),
      );
    }
  }

  return byRole;
}

function LeaderBoard({
  metricKey,
  rows,
}: {
  metricKey: ReplayMetricKey;
  rows: LeaderRow[];
}) {
  const t = useTranslations("Profile.behaviorMetrics");

  return (
    <div className="rounded-[13px] border border-surface-inset-border bg-surface-inset px-4 pb-3.5 pt-[15px]">
      <div className="mb-3 flex items-baseline justify-between gap-2.5">
        <span className="font-heading text-[17px] font-semibold tracking-[-0.005em]">
          {t(`axis.${metricKey}`)}
        </span>
        {/* Подпись единицы: без неё «0.61» ничего не значит. */}
        <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          {t(`unit.${metricKey}`)}
        </span>
      </div>

      <div className="flex flex-col gap-2.5">
        {rows.map((row, index) => (
          <div
            key={`${row.player.userId ?? row.player.riotIdGameName}-${index}`}
            className="grid grid-cols-[14px_32px_minmax(0,1fr)_48px_32px] items-center gap-2"
          >
            <span className="text-[11px] font-bold tabular-nums text-muted-foreground">
              {index + 1}.
            </span>
            {/* Аватар ОДИН и настоящий. До этого в строке стояли два кружка подряд:
                буквенная заглушка от борда и настоящий аватар от подписи игрока —
                заглушка читалась как второй, безымянный участник. */}
            <Avatar className="h-8 w-8">
              <AvatarImage
                src={row.player.avatarUrl ?? undefined}
                alt={row.player.displayName ?? row.player.riotIdGameName ?? "?"}
              />
              <AvatarFallback className="bg-muted text-[11px] font-bold text-muted-foreground">
                {(row.player.displayName ?? row.player.riotIdGameName ?? "?")
                  .charAt(0)
                  .toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="flex min-w-0 flex-col gap-1">
              <span className="truncate text-[13px] font-semibold">
                {/* KAN-743: имя профиля со ссылкой, Riot-ник — у тех, кого на сайте нет.
                    Без аватара: он стоит отдельной колонкой слева. */}
                <MetricsPlayerLabel player={row.player} showAvatar={false} />
              </span>
              <span className="block h-[5px] overflow-hidden rounded-full bg-muted">
                <span
                  className="block h-full rounded-full"
                  style={{
                    width: `${row.score}%`,
                    background: scoreColor(row.score),
                  }}
                />
              </span>
            </span>
            <span className="text-right text-xs tabular-nums text-muted-foreground">
              {formatMetricValue(metricKey, row.value)}
            </span>
            <span
              className="text-right font-heading text-base font-semibold tabular-nums"
              style={{ color: scoreColor(row.score) }}
            >
              {row.score}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BehaviorBoards({ tournamentId }: { tournamentId: string }) {
  const tl = useTranslations("TournamentStats.behaviorLeaders");
  const { data, isLoading, isError } = useTournamentReplayMetrics(tournamentId);

  const byRole = useMemo(() => buildBoards(data ?? []), [data]);

  /** Роли, у которых есть хотя бы один непустой борд. Остальные недоступны. */
  const roles = useMemo(() => {
    const present = [...byRole.entries()]
      .filter(([, boards]) => [...boards.values()].some((rows) => rows.length > 0))
      .map(([role]) => role);

    return present.sort((a, b) => {
      const ai = ROLE_ORDER.indexOf(a);
      const bi = ROLE_ORDER.indexOf(b);
      // Незнакомая роль не теряется — уходит в конец по алфавиту.
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [byRole]);

  const [selected, setSelected] = useState<string | null>(null);
  const role = selected && roles.includes(selected) ? selected : (roles[0] ?? null);

  if (isLoading || isError || !data || data.length === 0) return null;
  // Ни одной роли с пригодными данными — блока нет совсем. Пустые топы читаются
  // как «никто ничего не делал», а правда здесь — «выборка не набрана».
  if (role === null) return null;

  const boards = BOARD_KEYS.map((key) => ({
    key,
    rows: byRole.get(role)?.get(key) ?? [],
  })).filter((board) => board.rows.length > 0);

  if (boards.length === 0) return null;

  return (
    <section className="rounded-2xl border border-border bg-card p-[22px]">
      <div className="mb-[18px] flex flex-wrap items-start justify-between gap-4">
        <div>
          <SectionHeading title={tl("title")} />
          {/* Оговорка текстом, а не подсказкой: она объясняет, почему кого-то нет
              в списке, и прочитать её нужно до того, как возникнет вопрос. */}
          <p className="max-w-[520px] text-[13px] text-muted-foreground">
            {tl("subtitle")}
          </p>
        </div>

        <div
          role="tablist"
          aria-label={tl("roleFilterLabel")}
          className="flex gap-1 rounded-[11px] border border-surface-inset-border bg-surface-inset p-1"
        >
          {roles.map((r) => {
            const active = r === role;
            return (
              <button
                key={r}
                type="button"
                role="tab"
                aria-selected={active}
                title={r}
                aria-label={r}
                onClick={() => setSelected(r)}
                className={cn(
                  "flex items-center justify-center rounded-lg px-3 py-2 transition-colors",
                  active
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-accent",
                )}
              >
                <RoleIcon role={r} size={20} />
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(316px,1fr))]">
        {boards.map(({ key, rows }) => (
          <LeaderBoard key={key} metricKey={key} rows={rows} />
        ))}
      </div>
    </section>
  );
}
